import { z } from 'zod';

/**
 * AnalysisInputSchema v1 — frozen in Sprint 6.
 *
 * Single source of truth for:
 *   - Channel 1 wizard payload (apps/web) — 8 steps, each validated by its
 *     own step schema exported below.
 *   - Channel 2 intake form submission target (apps/intake)
 *   - Channel 3 API ingest body (POST /api/v1/orgs/:orgId/analyses/ingest)
 *   - packages/calc-engine runAnalysis input (via toRunAnalysisInputs adapter)
 *
 * The schema is organized into 8 sections that map 1:1 to the wizard steps.
 * Default values match docs/specs/economic-impact-formulas.md and
 * packages/calc-engine/src/construction-phase/types.ts SPEC_DEFAULTS.
 *
 * FROZEN: any additions or breaking changes after Sprint 6 require an ADR.
 * See ADR-003 (one Zod source, two transports).
 */

/* ===================================================================== */
/*  Step 1 — Project Description                                          */
/* ===================================================================== */

export const ProjectTypeEnum = z.enum(['new_startup', 'expansion', 'retention']);
export type ProjectTypeEnum = z.infer<typeof ProjectTypeEnum>;

export const WizardStep1 = z.object({
  projectName: z.string().min(1).max(200),
  projectType: ProjectTypeEnum,
  industryDescription: z.string().min(1).max(500),
  /**
   * 6-character industry code. Accepts either a 6-digit NAICS code
   * (e.g. "336111") or a BEA I/O aggregate used by RIMS II
   * (e.g. "2122A0", "33399B"). When the wizard uses the RIMS-driven
   * industry dropdown, this stores the BEA I/O code directly so the
   * calc engine skips the fuzzy NAICS→BEA match step.
   */
  naicsCode: z
    .string()
    .regex(/^[0-9A-Z]{6}$/, 'Industry code must be 6 characters (NAICS or BEA I/O code)'),
  city: z.string().min(1).max(100),
  county: z.string().min(1).max(100),
  state: z.string().length(2).default('TX'),
  schoolDistrict: z.string().max(200).optional(),
  specialDistrict: z.string().max(200).optional(),
  analysisPeriodYears: z.number().int().min(1).max(30).default(10),
  /** Nanoid of the rims_dataset to use for multiplier lookup. */
  rimsDatasetId: z.string().optional(),
});
export type WizardStep1 = z.infer<typeof WizardStep1>;

/* ===================================================================== */
/*  Step 2 — Capital Investment                                           */
/* ===================================================================== */

export const WizardStep2 = z.object({
  /** Total construction hard costs (buildings + site work) in USD. */
  totalConstructionSpending: z.number().nonnegative(),
  land: z.number().nonnegative().default(0),
  buildings: z.number().nonnegative().default(0),
  machineryEquipment: z.number().nonnegative().default(0),
  ffe: z.number().nonnegative().default(0),
  inventory: z.number().nonnegative().default(0),
  /** Duration of construction in months, used for permit/fee timing. */
  constructionDurationMonths: z.number().int().positive().default(12),

  /**
   * ADR-030 baselines — existing values prior to the expansion.
   * For projectType === 'expansion': analysts enter their current baseline
   *   so the adapter passes only the incremental delta to the calc engine.
   * For projectType === 'new_startup' or 'retention': these remain 0 and
   *   the adapter treats the reported values as the full impact.
   * All default to 0 so existing analyses migrate cleanly.
   */
  baselineTotalConstructionSpending: z.number().nonnegative().default(0),
  baselineLand: z.number().nonnegative().default(0),
  baselineBuildings: z.number().nonnegative().default(0),
  baselineMachineryEquipment: z.number().nonnegative().default(0),
  baselineFfe: z.number().nonnegative().default(0),
  baselineInventory: z.number().nonnegative().default(0),
});
export type WizardStep2 = z.infer<typeof WizardStep2>;

/* ===================================================================== */
/*  Step 3 — Employment                                                   */
/* ===================================================================== */

export const WizardStep3 = z.object({
  /** Number of direct full-time-equivalent jobs at full ramp-up. */
  directJobs: z.number().nonnegative(),
  /** Annual payroll at full stabilization in USD. */
  directAnnualPayroll: z.number().nonnegative(),
  /** Annual payroll growth rate, 0..1. Default 2%. */
  payrollGrowthRate: z.number().min(0).max(0.5).default(0.02),
  /** Staffing ramp-up factors: fraction of full jobs in year 1, 2, 3+. */
  staffingRampYear1: z.number().min(0).max(1).default(0.7),
  staffingRampYear2: z.number().min(0).max(1).default(0.9),

  /** ADR-030 baselines — existing employment prior to the expansion. */
  baselineDirectJobs: z.number().nonnegative().default(0),
  baselineDirectAnnualPayroll: z.number().nonnegative().default(0),
});
export type WizardStep3 = z.infer<typeof WizardStep3>;

/* ===================================================================== */
/*  Step 4 — Revenue                                                      */
/* ===================================================================== */

export const WizardStep4 = z.object({
  /** Gross annual taxable sales at stabilization in USD. */
  annualTaxableSales: z.number().nonnegative(),
  /** Sales growth rate, 0..1. Default 2%. */
  salesGrowthRate: z.number().min(0).max(0.5).default(0.02),
  /** Revenue ramp-up factors: fraction of full sales in year 1, 2, 3+. */
  revenueRampYear1: z.number().min(0).max(1).default(0.6),
  revenueRampYear2: z.number().min(0).max(1).default(0.9),
  /** Annual gross room revenue at stabilization (lodging only, 0 otherwise). */
  annualRoomRevenue: z.number().nonnegative().default(0),
  /** Annual mixed-beverage receipts at stabilization (F&B/hotels, 0 otherwise). */
  annualMixedBeverageReceipts: z.number().nonnegative().default(0),

  /** ADR-030 baselines — existing revenue prior to the expansion. */
  baselineAnnualTaxableSales: z.number().nonnegative().default(0),
  baselineAnnualRoomRevenue: z.number().nonnegative().default(0),
  baselineAnnualMixedBeverageReceipts: z.number().nonnegative().default(0),
});
export type WizardStep4 = z.infer<typeof WizardStep4>;

/* ===================================================================== */
/*  Step 5 — Property & Assessment                                        */
/* ===================================================================== */

export const WizardStep5 = z.object({
  /** Year-1 assessed value of real property (land + buildings) in USD. */
  assessedRealProperty: z.number().nonnegative(),
  /** Year-1 assessed value of business personal property (equipment, etc.). */
  assessedBpp: z.number().nonnegative(),
  /** Year-1 assessed value of business inventory. */
  assessedInventory: z.number().nonnegative().default(0),
  /** Annual appreciation rate on real property, 0..1. Default 2%. */
  realPropertyAppreciationRate: z.number().min(-0.1).max(0.5).default(0.02),
  /** Annual depreciation rate on BPP, 0..1. Default 10%. */
  bppDepreciationRate: z.number().min(0).max(0.5).default(0.1),
  /** Annual growth rate on inventory, 0..1. Default 2%. */
  inventoryGrowthRate: z.number().min(0).max(0.5).default(0.02),

  /** ADR-030 baselines — existing assessed value prior to the expansion. */
  baselineAssessedRealProperty: z.number().nonnegative().default(0),
  baselineAssessedBpp: z.number().nonnegative().default(0),
  baselineAssessedInventory: z.number().nonnegative().default(0),
});
export type WizardStep5 = z.infer<typeof WizardStep5>;

/* ===================================================================== */
/*  Step 6 — Jurisdiction & Tax Rates                                     */
/* ===================================================================== */

/**
 * City tax rates. Stored on the JurisdictionProfile in Sprint 7; entered
 * manually in the wizard for Sprint 6.
 */
export const WizardStep6 = z.object({
  /**
   * City portion of sales tax rate, decimal (e.g., 0.02 for 2%). Cap covers
   * the highest US city local-option rates (~7% in parts of AK) with
   * headroom; per-state legal caps arrive with the State Record (ADR-033).
   */
  salesTaxRate: z.number().min(0).max(0.08).default(0.02),
  /** City effective property tax rate, decimal (not per-$100 form). */
  propertyTaxRate: z.number().min(0).max(0.05).default(0.005),
  /** Hotel Occupancy Tax rate, decimal. Default 7%. */
  hotelOccupancyTaxRate: z.number().min(0).max(0.25).default(0.07),
  /**
   * Effective city revenue rate on mixed beverage receipts, decimal.
   * The engine computes `mixedBeverageSales x mixedBeverageShareRate`, so this
   * must be an effective rate on gross receipts, NOT the 10.7143% city share
   * of state collections (TX Tax Code ch. 183). Default: 10.7143% city
   * allocation x 14.95% combined state MB taxes (6.7% gross receipts +
   * 8.25% MB sales) = 0.016018 (national-expansion-plan 0.1).
   * Cap stays 0.25: pre-fix analysis snapshots carry 0.107143 and are
   * re-parsed by sensitivity/scenario paths, so a tighter cap would reject
   * historical inputs.
   */
  mixedBeverageShareRate: z.number().min(0).max(0.25).default(0.016018),

  /**
   * ADR-034 2.1 — local wage/income tax rate on direct payroll, decimal.
   * Default 0 (TX and other no-local-income-tax states), so the wage-tax
   * revenue line is zero and TX stays byte-identical. OH/PA municipalities
   * run ~1-3%.
   */
  localWageTaxRate: z.number().min(0).max(0.05).default(0),
  /**
   * ADR-034 2.1 — share of direct payroll subject to the local wage tax
   * (resident/taxable share), 0..1. Default 1.0. Only affects the result
   * when localWageTaxRate > 0.
   */
  residentTaxableShare: z.number().min(0).max(1).default(1),

  /** Annual misc taxes/fees per worker (from CAFR). */
  miscTaxesFeesPerWorker: z.number().nonnegative().default(150),
  /** Annual household taxable spending per relocating worker. */
  householdTaxableSpendingPerWorker: z.number().nonnegative().default(12000),
  /** Annual utility franchise fee per job. */
  utilityFranchiseFeePerJob: z.number().nonnegative().default(100),

  /** Base annual cost of government services per new worker (city). */
  baseCityCostPerWorker: z.number().nonnegative().default(1200),
  /** Base annual cost per new worker (county). */
  baseCountyCostPerWorker: z.number().nonnegative().default(800),
  /** Base annual cost per new student (ISD). */
  baseIsdCostPerStudent: z.number().nonnegative().default(6000),
  /** Annual cost inflation rate, 0..1. Default 3%. */
  costInflationRate: z.number().min(0).max(0.2).default(0.03),

  /** County effective property tax rate, decimal (e.g., 0.004 for 0.4%). Default 0.4%. */
  countyPropertyTaxRate: z.number().min(0).max(0.05).default(0.004),
  /** ISD effective property tax rate, decimal. Default 1.3%. */
  isdPropertyTaxRate: z.number().min(0).max(0.05).default(0.013),
  /**
   * ADR-034 2.3 — share of ISD property-tax levy the local district keeps
   * after state recapture / aid offset, 0..1. Default 1.0 (no recapture)
   * keeps TX byte-identical. Recapture states (and TX Robin Hood, if ever
   * modeled) run below 1.0.
   */
  schoolLocalRetentionShare: z.number().min(0).max(1).default(1),
  /** Optional special district name (e.g., "Hill Country WCID #1"). Omit for no special district. */
  specialDistrictName: z.string().max(200).optional(),
  /** Special district property tax rate, decimal. Required if specialDistrictName is present. */
  specialDistrictPropertyTaxRate: z.number().min(0).max(0.05).optional(),
  /** Annual special district cost per direct worker. Required if specialDistrictName is present. */
  specialDistrictCostPerWorker: z.number().nonnegative().optional(),

  // ── ADR-034: taxability + assessment (adapter-consumed, engine unchanged) ──
  /**
   * Whether business personal property is subject to property tax. Most states
   * tax BPP; ~a dozen exempt it. When false the adapter zeroes the assessed BPP
   * value before the engine sees it. Default true preserves TX.
   */
  taxesBpp: z.boolean().default(true),
  /**
   * Whether business inventory is subject to property tax. Most states exempt
   * inventory. When false the adapter zeroes the assessed inventory value.
   * Default true preserves TX.
   */
  taxesInventory: z.boolean().default(true),
  /**
   * Classification ratio applied to real-property market value to reach the
   * taxable assessed value (assessed = market x ratio). 1.0 = assessed equals
   * market (TX). Classified-assessment states use a fraction (e.g. 0.35).
   */
  assessmentRatioReal: z.number().min(0).max(1).default(1),
  /** Classification ratio for BPP. Default 1.0 preserves TX. */
  assessmentRatioBpp: z.number().min(0).max(1).default(1),
  /** Classification ratio for inventory. Default 1.0 preserves TX. */
  assessmentRatioInventory: z.number().min(0).max(1).default(1),

  // ── ADR-036: jurisdiction tier presence (state-level, adapter-consumed) ──
  /**
   * Whether the jurisdiction has a county property-tax tier. False for New
   * England towns and consolidated city-counties; the adapter then omits the
   * county impact entirely. Default true preserves TX.
   */
  hasCountyPropertyTax: z.boolean().default(true),
  /**
   * Whether schools are a separate property-taxing district. False where the
   * school layer is a city department; the adapter omits the ISD impact.
   * Default true preserves TX.
   */
  hasSeparateSchoolDistrict: z.boolean().default(true),
});
export type WizardStep6 = z.infer<typeof WizardStep6>;

/* ===================================================================== */
/*  Step 7 — Assumptions (RIMS & Local Spending)                          */
/* ===================================================================== */

/**
 * RIMS II multipliers for the selected industry + region. The wizard
 * auto-populates these via the NAICS→BEA fuzzy-match lookup against the
 * uploaded rims_dataset. The analyst can override any value.
 *
 * Operations multipliers are Type II direct-effect (jobs-per-direct-job,
 * earnings-per-direct-$). Construction multipliers are final-demand.
 * See docs/specs/economic-impact-formulas.md §4 and §5.
 */
export const WizardStep7 = z.object({
  /** Type II DE employment multiplier: total jobs per direct job (operations). */
  operationsEmploymentMultiplier: z.number().positive(),
  /** Type II DE earnings multiplier: $ of total earnings per $ of direct earnings (operations). */
  operationsEarningsMultiplier: z.number().positive(),
  /** RIMS II FD employment multiplier for construction: jobs per $1M of demand. */
  constructionEmploymentMultiplierJobsPerMillion: z.number().positive(),
  /** RIMS II FD earnings multiplier for construction: $ of earnings per $ of demand. */
  constructionEarningsMultiplier: z.number().positive(),
  /** RIMS II FD output multiplier for construction: $ of output per $ of demand. */
  constructionOutputMultiplier: z.number().positive(),

  /** Fraction of construction spend captured within local economy, 0..1. Default 50%. */
  localConstructionCaptureRate: z.number().min(0).max(1).default(0.5),
  /** Fraction of worker earnings spent locally, 0..1. Default 50%. */
  workerLocalCaptureRate: z.number().min(0).max(1).default(0.5),
  /** Fraction of local worker spending that is taxable, 0..1. Default 60%. */
  workerTaxableSpendingShare: z.number().min(0).max(1).default(0.6),
  /** Fraction of construction spend that is materials, 0..1. Default 50%. */
  materialsShareOfConstruction: z.number().min(0).max(1).default(0.5),
  /** Fraction of materials spend subject to local sales tax, 0..1. Default 80%. */
  materialsTaxabilityRate: z.number().min(0).max(1).default(0.8),
  /** Permits and fees as a fraction of construction spending, 0..1. Default 0.5%. */
  permitFeeRate: z.number().min(0).max(0.1).default(0.005),
  /** Displacement rate: fraction of new activity that displaces existing, 0..1. Default 10%. */
  displacementRate: z.number().min(0).max(1).default(0.1),
  /** Fraction of direct workers who relocate (vs. commuting in), 0..1. Default 20%. */
  workerRelocationPct: z.number().min(0).max(1).default(0.2),
  /** Average number of people per household for relocating workers. Default 2.5. */
  averageHouseholdSize: z.number().positive().default(2.5),
  /** School-age children per household of a relocating worker. Default 0.5. */
  schoolAgeChildrenPerHousehold: z.number().nonnegative().default(0.5),
});
export type WizardStep7 = z.infer<typeof WizardStep7>;

/* ===================================================================== */
/*  Step 8 — Incentive Package                                            */
/* ===================================================================== */

export const WizardStep8 = z.object({
  /** Job grant as a fraction of annual direct payroll, 0..1. Default 0 (none). */
  jobGrantPctOfPayroll: z.number().min(0).max(1).default(0),
  /** Years of job grant payments. Default 0. */
  jobGrantYears: z.number().int().nonnegative().default(0),
  /**
   * ADR-035 3.3 — what the job grant is a percentage of. 'payroll' (default)
   * is today's behavior; 'withholding' (NC JDIG) scales by
   * effectiveWithholdingRate.
   */
  jobGrantBase: z.enum(['payroll', 'withholding']).default('payroll'),
  /** Effective income-tax withholding rate on payroll, 0..1. Default 0. */
  effectiveWithholdingRate: z.number().min(0).max(1).default(0),
  /** Total infrastructure grant in USD. Default 0. */
  infrastructureGrantTotal: z.number().nonnegative().default(0),
  /** Years over which the infrastructure grant is distributed. Default 0. */
  infrastructureGrantYears: z.number().int().nonnegative().default(0),
  /** Fraction of real property tax abated, 0..1. Default 0. */
  propertyTaxAbatementPct: z.number().min(0).max(1).default(0),
  /** Years of property tax abatement. Default 0. */
  propertyTaxAbatementYears: z.number().int().nonnegative().default(0),
  /**
   * Stepped/declining abatement schedule — one abated fraction (0..1) per
   * analysis year, e.g. [1, 1, 0.8, 0.8, 0.6, 0.6, 0.4, 0.4, 0.2, 0.2] for a
   * 10-year 100/100/80/80/60/60/40/40/20/20 abatement (a common real-world
   * shape that a flat pct/years pair cannot express). When present,
   * OVERRIDES propertyTaxAbatementPct/propertyTaxAbatementYears entirely for
   * the standard abatement. Optional and undefined by default, so existing
   * flat-pct/years analyses are byte-identical. Max length matches the
   * longest allowed analysis period (see step1.analysisPeriodYears).
   */
  propertyTaxAbatementSchedule: z.array(z.number().min(0).max(1)).max(30).optional(),
  /**
   * ADR-035 3.2 — what the abatement percentage applies to:
   *   'increment' (default, TX Ch. 312): the net-new value only.
   *   'full': the full assessed value (project + baseline) — for statutes
   *   that abate full value on an expansion. Same as increment for a new
   *   startup (no baseline).
   */
  abatementBase: z.enum(['increment', 'full']).default('increment'),
  /**
   * ADR-035 3.1 (PILOT / leaseback) — effective share of real-property tax
   * abated by a PILOT, 0..1. Unlike the standard abatement it applies across
   * every tier (city, county, ISD), so one authority (a TN IDB) can abate the
   * overlapping levies. Default 0. Common-case model: a flat in-lieu schedule
   * is a follow-on. Set EITHER this OR propertyTaxAbatementPct, not both.
   */
  pilotAbatementPct: z.number().min(0).max(1).default(0),
  /** Years the PILOT abatement runs. Default 0. */
  pilotAbatementYears: z.number().int().nonnegative().default(0),
  /**
   * ADR-035 3.4 — share of the project's city sales tax rebated to the
   * company, 0..1. Default 0 (no rebate) keeps TX byte-identical.
   */
  salesTaxRebatePct: z.number().min(0).max(1).default(0),
  /** Years the sales-tax rebate is paid. Default 0. */
  salesTaxRebateYears: z.number().int().nonnegative().default(0),
  /**
   * ADR-035 (simplified TIF) — share of the incremental real-property tax
   * captured into a TIF district fund, 0..1. Default 0. Memo only: does not
   * reduce net benefit.
   */
  tifCaptureShare: z.number().min(0).max(1).default(0),
  /** Years the TIF captures the increment. Default 0. */
  tifYears: z.number().int().nonnegative().default(0),
  /**
   * ADR-035 3.5 (AZ GPLET) — effective real-property rate substituting for ad
   * valorem (the $/sq-ft excise expressed as a rate). null = off (default), so
   * real property uses the normal rate. AZ-specific. NOTE (documented
   * limitation): applied to the CITY real-property line only; full county/ISD
   * ad-valorem substitution is a follow-on.
   */
  gpletEffectivePropertyRate: z.number().min(0).max(0.1).nullable().default(null),
  /**
   * ADR-035 3.6 — statutory approval gate for the incentive package. Metadata
   * only: drives a wizard flag and (future) a report footnote, no math.
   */
  approvalGate: z.enum(['none', 'school_board', 'referendum', 'state_review']).default('none'),
  /** Present-value discount rate, 0..1. Default 5%. */
  discountRate: z.number().min(0).max(0.3).default(0.05),
});
export type WizardStep8 = z.infer<typeof WizardStep8>;

/* ===================================================================== */
/*  ADR-031 — Retention counterfactual                                    */
/* ===================================================================== */

/**
 * Retention counterfactual — the "what would happen without the incentive"
 * story for retention projects. See ADR-031 Part A.
 *
 * OPTIONAL in the Zod schema so historical analyses validate, but REQUIRED
 * at wizard submission and Channel 3 ingest when
 * `step1.projectType === 'retention'`. Enforced by
 * `isRetentionCounterfactualMissing()` below.
 */
export const RetentionCounterfactualSchema = z.object({
  /**
   * Probability (0.0–1.0) that activity departs the jurisdiction absent
   * the incentive. Highest-leverage knob in a retention analysis.
   * `LOCKED_FIELDS` entry per ADR-031 Part D.
   */
  probabilityOfDeparture: z.number().min(0).max(1),

  /**
   * Year (1–10) in which departure would occur absent intervention. The
   * adapter builds a step-function timing mask that is 0 before this year
   * and 1 from this year onward.
   */
  departureTimingYear: z.number().int().min(1).max(10),

  /**
   * Fraction (0.0–1.0) of activity that remains even if the firm departs
   * — inventory, rolling stock, or real property that stays on the tax
   * roll. Near zero for jobs/payroll; can be meaningful for real property.
   */
  retainedSharePostDeparture: z.number().min(0).max(1),

  /**
   * Fraction (0.0–1.0) of jobs reabsorbed by other local employers within
   * a 3-year window if the firm leaves. Seeded from the org-level regional
   * default; per-analysis override allowed. Mirrors greenfield displacement
   * in the opposite direction: high absorption shrinks avoided-loss
   * because the economy recovers without intervention.
   */
  localAbsorptionRate: z.number().min(0).max(1),

  /**
   * Free-text justification. REQUIRED at submission when
   * `probabilityOfDeparture > 0.5` — enforced by
   * `isRetentionEvidenceMissing()` below (Phase 4 wires the 400 response).
   */
  evidence: z.string().max(2000).optional(),
});
export type RetentionCounterfactual = z.infer<typeof RetentionCounterfactualSchema>;

/* ===================================================================== */
/*  Top-level frozen schema                                               */
/* ===================================================================== */

export const AnalysisInputSchema = z.object({
  step1: WizardStep1,
  step2: WizardStep2,
  step3: WizardStep3,
  step4: WizardStep4,
  step5: WizardStep5,
  step6: WizardStep6,
  step7: WizardStep7,
  step8: WizardStep8,
  /** ADR-031 — present iff projectType === 'retention'; see validators below. */
  retentionCounterfactual: RetentionCounterfactualSchema.optional(),
});

export type AnalysisInput = z.infer<typeof AnalysisInputSchema>;

/* ===================================================================== */
/*  Wizard step registry (for save-as-draft routing)                      */
/* ===================================================================== */

/** All 8 step schemas in order, for the wizard's step-by-step validation. */
export const WIZARD_STEPS = [
  WizardStep1,
  WizardStep2,
  WizardStep3,
  WizardStep4,
  WizardStep5,
  WizardStep6,
  WizardStep7,
  WizardStep8,
] as const;

export type WizardStepIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;
export type WizardStepSchema = (typeof WIZARD_STEPS)[WizardStepIndex];

/* ===================================================================== */
/*  Adapter: AnalysisInput → RunAnalysisInputs                           */
/*                                                                        */
/*  Bridges the wizard's flat schema to the calc engine's module-sliced  */
/*  input shape. Lives here (in packages/shared) so both the API and     */
/*  any future Web Worker can import it without pulling in the full       */
/*  calc-engine package.                                                  */
/*                                                                        */
/*  See ADR-026 (orchestration contract).                                 */
/* ===================================================================== */

export interface AdaptedAnalysisInputs {
  readonly construction: {
    readonly totalConstructionSpending: number;
    readonly localConstructionCaptureRate: number;
    readonly constructionOutputMultiplier: number;
    readonly constructionEarningsMultiplier: number;
    readonly constructionEmploymentMultiplierJobsPerMillion: number;
    readonly localSalesTaxRate: number;
    readonly workerLocalCaptureRate: number;
    readonly workerTaxableSpendingShare: number;
    readonly materialsShareOfConstruction: number;
    readonly materialsTaxabilityRate: number;
    readonly permitFeeRate: number;
  };
  readonly projections: {
    readonly directJobs: number;
    readonly directAnnualPayroll: number;
    readonly annualTaxableSales: number;
    readonly operationsEmploymentMultiplier: number;
    readonly operationsEarningsMultiplier: number;
    readonly assessedRealProperty: number;
    readonly assessedBpp: number;
    readonly assessedInventory: number;
    readonly realPropertyAppreciationRate: number;
    readonly bppDepreciationRate: number;
    readonly inventoryGrowthRate: number;
    readonly payrollGrowthRate: number;
    readonly salesGrowthRate: number;
    readonly displacementRate: number;
    readonly annualRoomRevenue: number;
    readonly annualMixedBeverageReceipts: number;
    readonly staffingRamp: {
      readonly year1: number;
      readonly year2: number;
      readonly year3Plus: number;
    };
    readonly revenueRamp: {
      readonly year1: number;
      readonly year2: number;
      readonly year3Plus: number;
    };
    readonly baseCityCostPerWorker: number;
    readonly baseCountyCostPerWorker: number;
    readonly baseIsdCostPerStudent: number;
    readonly costInflationRate: number;
    readonly workerRelocationPct: number;
    readonly averageHouseholdSize: number;
    readonly schoolAgeChildrenPerHousehold: number;
    readonly analysisPeriodYears: number;
  };
  readonly cityRates: {
    readonly salesTaxRate: number;
    readonly propertyTaxRate: number;
    readonly hotelOccupancyTaxRate: number;
    readonly mixedBeverageShareRate: number;
    readonly localWageTaxRate: number;
    readonly residentTaxableShare: number;
    readonly realPropertyTaxRate?: number;
  };
  readonly cityCosts: {
    readonly miscTaxesFeesPerWorker: number;
    readonly householdTaxableSpendingPerWorker: number;
    readonly utilityFranchiseFeePerJob: number;
  };
  readonly incentive: {
    readonly jobGrantPctOfPayroll: number;
    readonly jobGrantYears: number;
    readonly jobGrantBase: 'payroll' | 'withholding';
    readonly effectiveWithholdingRate: number;
    readonly infrastructureGrantTotal: number;
    readonly infrastructureGrantYears: number;
    readonly propertyTaxAbatementPct: number;
    readonly propertyTaxAbatementYears: number;
    readonly propertyTaxAbatementSchedule?: readonly number[];
    readonly abatementValueMultiplier: number;
    readonly pilotAbatementPct: number;
    readonly pilotAbatementYears: number;
    readonly salesTaxRebatePct: number;
    readonly salesTaxRebateYears: number;
    readonly tifCaptureShare: number;
    readonly tifYears: number;
    readonly discountRate: number;
  };
  readonly countyRates?: {
    readonly propertyTaxRate: number;
    readonly discountRate: number;
    readonly abatementPct: number;
    readonly abatementYears: number;
  };
  readonly isdRates?: {
    readonly propertyTaxRate: number;
    readonly discountRate: number;
    readonly localRetentionShare: number;
    readonly abatementPct: number;
    readonly abatementYears: number;
  };
  readonly specialDistrictRates?: {
    readonly name: string;
    readonly propertyTaxRate: number;
    readonly discountRate: number;
    readonly costPerWorker: number;
    readonly costInflationRate: number;
  };
}

/**
 * Compute the incremental value for a baseline/reported pair (ADR-030).
 * For new_startup analyses (where baseline=0) this is a no-op; for expansion
 * it returns the delta above the existing baseline. Always floors at 0 so a
 * typo (baseline > reported) cannot feed a negative into the engine.
 */
function incremental(reported: number, baseline: number): number {
  return Math.max(0, reported - baseline);
}

/**
 * ADR-031 — project the at-risk value into an "avoided loss" value for a
 * jobs/payroll/sales/receipts stream.
 *
 *   avoided = reported × P(departure) × (1 − localAbsorption) × 1{t ≥ departureYear}
 *
 * Because the engine consumes a single scalar per stream (not a vector),
 * the timing mask collapses here: streams whose departure year lies BEYOND
 * the analysis period yield zero (the loss never materializes inside the
 * horizon); streams whose departure year is within the horizon yield the
 * full risk-weighted value (engine-side ramp/growth handles the year-by-
 * year shape).
 *
 * `retainedSharePostDeparture` is not applied here — activity-side streams
 * (jobs/sales) do not "stay on the tax roll"; the retained-share knob is
 * used only for real property in `avoidedPropertyValue`.
 */
function avoidedActivityValue(
  reported: number,
  cf: RetentionCounterfactual,
  analysisPeriodYears: number,
): number {
  if (cf.departureTimingYear > analysisPeriodYears) return 0;
  const absorption = Math.min(1, Math.max(0, cf.localAbsorptionRate));
  const p = Math.min(1, Math.max(0, cf.probabilityOfDeparture));
  return Math.max(0, reported * p * (1 - absorption));
}

/**
 * ADR-031 — project the at-risk real property value into avoided loss.
 * Same as `avoidedActivityValue` but applies `retainedSharePostDeparture`
 * (inventory, rolling stock, or property that stays on the tax roll even
 * after departure) instead of local absorption.
 */
function avoidedPropertyValue(
  reported: number,
  cf: RetentionCounterfactual,
  analysisPeriodYears: number,
): number {
  if (cf.departureTimingYear > analysisPeriodYears) return 0;
  const retained = Math.min(1, Math.max(0, cf.retainedSharePostDeparture));
  const p = Math.min(1, Math.max(0, cf.probabilityOfDeparture));
  return Math.max(0, reported * p * (1 - retained));
}

/**
 * Convert a validated AnalysisInput (from the wizard or API) into the
 * module-sliced shape expected by runAnalysis() in packages/calc-engine.
 *
 * Pure function — no I/O, no side effects. Safe to call in a Web Worker.
 *
 * ADR-030: when a baseline is non-zero (expansion projects), the adapter
 * passes only the *incremental* delta to the engine. The calc engine
 * signature is unchanged; the baseline concept does not exist inside the
 * engine itself.
 */
export function toRunAnalysisInputs(i: AnalysisInput): AdaptedAnalysisInputs {
  // ADR-031: retention analyses project avoided-loss vectors into the same
  // input slots the engine consumes for greenfield. The engine never learns
  // project type; the adapter owns the branch.
  const isRetention = i.step1.projectType === 'retention';
  const cf = i.retentionCounterfactual;
  const horizon = i.step1.analysisPeriodYears;

  // ADR-035 3.2 — abatement base multiplier. The engine abates the increment
  // real-property tax; 'full' lifts that to the full assessed value. Because
  // net-new and baseline appreciate at the same rate (and any assessment ratio
  // cancels), full/increment = reported / (reported - baseline). Only for a
  // non-retention project with a positive increment; else 1 (increment).
  const abatementValueMultiplier = (() => {
    if (i.step8.abatementBase !== 'full' || isRetention) return 1;
    const reported = i.step5.assessedRealProperty;
    const increment = reported - i.step5.baselineAssessedRealProperty;
    return increment > 0 ? reported / increment : 1;
  })();

  // Pre-ADR-031 retention analyses (or brand-new ones pending wizard
  // completion) have no counterfactual: per ADR-031 Part E Option 1 we zero
  // out all retention streams. The report layer renders a footer explaining
  // that no counterfactual was captured.
  const activity = (reported: number, baseline: number): number => {
    if (isRetention) {
      return cf ? avoidedActivityValue(reported, cf, horizon) : 0;
    }
    return incremental(reported, baseline);
  };
  const property = (reported: number, baseline: number): number => {
    if (isRetention) {
      return cf ? avoidedPropertyValue(reported, cf, horizon) : 0;
    }
    return incremental(reported, baseline);
  };
  // ADR-031 Part B: retention projects almost never involve net-new capex;
  // ADR-032 marked `capitalInvestment` N/A for retention. Zero out
  // construction streams so the engine's construction-phase module produces
  // no spurious multiplier output.
  const construction = (reported: number, baseline: number): number => {
    if (isRetention) return 0;
    return incremental(reported, baseline);
  };

  return {
    construction: {
      totalConstructionSpending: construction(
        i.step2.totalConstructionSpending,
        i.step2.baselineTotalConstructionSpending,
      ),
      localConstructionCaptureRate: i.step7.localConstructionCaptureRate,
      constructionOutputMultiplier: i.step7.constructionOutputMultiplier,
      constructionEarningsMultiplier: i.step7.constructionEarningsMultiplier,
      constructionEmploymentMultiplierJobsPerMillion:
        i.step7.constructionEmploymentMultiplierJobsPerMillion,
      localSalesTaxRate: i.step6.salesTaxRate,
      workerLocalCaptureRate: i.step7.workerLocalCaptureRate,
      workerTaxableSpendingShare: i.step7.workerTaxableSpendingShare,
      materialsShareOfConstruction: i.step7.materialsShareOfConstruction,
      materialsTaxabilityRate: i.step7.materialsTaxabilityRate,
      permitFeeRate: i.step7.permitFeeRate,
    },
    projections: {
      directJobs: activity(i.step3.directJobs, i.step3.baselineDirectJobs),
      directAnnualPayroll: activity(
        i.step3.directAnnualPayroll,
        i.step3.baselineDirectAnnualPayroll,
      ),
      annualTaxableSales: activity(i.step4.annualTaxableSales, i.step4.baselineAnnualTaxableSales),
      operationsEmploymentMultiplier: i.step7.operationsEmploymentMultiplier,
      operationsEarningsMultiplier: i.step7.operationsEarningsMultiplier,
      // ADR-034 2.4/2.2: assessed = market x classification ratio (applied to
      // project and baseline alike), then the taxability toggle zeroes an
      // exempt class entirely. Ratios default 1.0 and toggles default true, so
      // TX is byte-identical. The `??` fallbacks make historical analyses
      // (snapshots frozen before ADR-034) and any raw, un-parsed input reduce
      // to the TX behavior instead of producing NaN. Engine stays unchanged:
      // it only ever sees the resulting assessed scalars.
      assessedRealProperty: property(
        i.step5.assessedRealProperty * (i.step6.assessmentRatioReal ?? 1),
        i.step5.baselineAssessedRealProperty * (i.step6.assessmentRatioReal ?? 1),
      ),
      assessedBpp:
        (i.step6.taxesBpp ?? true)
          ? property(
              i.step5.assessedBpp * (i.step6.assessmentRatioBpp ?? 1),
              i.step5.baselineAssessedBpp * (i.step6.assessmentRatioBpp ?? 1),
            )
          : 0,
      assessedInventory:
        (i.step6.taxesInventory ?? true)
          ? property(
              i.step5.assessedInventory * (i.step6.assessmentRatioInventory ?? 1),
              i.step5.baselineAssessedInventory * (i.step6.assessmentRatioInventory ?? 1),
            )
          : 0,
      realPropertyAppreciationRate: i.step5.realPropertyAppreciationRate,
      bppDepreciationRate: i.step5.bppDepreciationRate,
      inventoryGrowthRate: i.step5.inventoryGrowthRate,
      payrollGrowthRate: i.step3.payrollGrowthRate,
      salesGrowthRate: i.step4.salesGrowthRate,
      displacementRate: i.step7.displacementRate,
      annualRoomRevenue: activity(i.step4.annualRoomRevenue, i.step4.baselineAnnualRoomRevenue),
      annualMixedBeverageReceipts: activity(
        i.step4.annualMixedBeverageReceipts,
        i.step4.baselineAnnualMixedBeverageReceipts,
      ),
      staffingRamp: {
        year1: i.step3.staffingRampYear1,
        year2: i.step3.staffingRampYear2,
        year3Plus: 1.0,
      },
      revenueRamp: {
        year1: i.step4.revenueRampYear1,
        year2: i.step4.revenueRampYear2,
        year3Plus: 1.0,
      },
      baseCityCostPerWorker: i.step6.baseCityCostPerWorker,
      baseCountyCostPerWorker: i.step6.baseCountyCostPerWorker,
      baseIsdCostPerStudent: i.step6.baseIsdCostPerStudent,
      costInflationRate: i.step6.costInflationRate,
      workerRelocationPct: i.step7.workerRelocationPct,
      averageHouseholdSize: i.step7.averageHouseholdSize,
      schoolAgeChildrenPerHousehold: i.step7.schoolAgeChildrenPerHousehold,
      analysisPeriodYears: i.step1.analysisPeriodYears,
    },
    cityRates: {
      salesTaxRate: i.step6.salesTaxRate,
      propertyTaxRate: i.step6.propertyTaxRate,
      hotelOccupancyTaxRate: i.step6.hotelOccupancyTaxRate,
      mixedBeverageShareRate: i.step6.mixedBeverageShareRate,
      // ADR-034 2.1 — `?? ` keeps pre-ADR-034 snapshots at the TX default (no
      // wage tax) instead of NaN when the frozen input predates these fields.
      localWageTaxRate: i.step6.localWageTaxRate ?? 0,
      residentTaxableShare: i.step6.residentTaxableShare ?? 1,
      // ADR-035 3.5 — GPLET excise overrides the city real-property rate.
      // null/undefined leaves real property on the normal rate (byte-identical).
      realPropertyTaxRate: i.step8.gpletEffectivePropertyRate ?? undefined,
    },
    cityCosts: {
      miscTaxesFeesPerWorker: i.step6.miscTaxesFeesPerWorker,
      householdTaxableSpendingPerWorker: i.step6.householdTaxableSpendingPerWorker,
      utilityFranchiseFeePerJob: i.step6.utilityFranchiseFeePerJob,
    },
    incentive: {
      jobGrantPctOfPayroll: i.step8.jobGrantPctOfPayroll,
      jobGrantYears: i.step8.jobGrantYears,
      // ADR-035 3.3 — `?? ` keeps pre-3.3 snapshots on the payroll base.
      jobGrantBase: i.step8.jobGrantBase ?? 'payroll',
      effectiveWithholdingRate: i.step8.effectiveWithholdingRate ?? 0,
      infrastructureGrantTotal: i.step8.infrastructureGrantTotal,
      infrastructureGrantYears: i.step8.infrastructureGrantYears,
      propertyTaxAbatementPct: i.step8.propertyTaxAbatementPct,
      propertyTaxAbatementYears: i.step8.propertyTaxAbatementYears,
      // Stepped schedule — passed through as-is; the engine treats it as an
      // override of the flat pct/years pair above. Undefined when not set.
      propertyTaxAbatementSchedule: i.step8.propertyTaxAbatementSchedule,
      abatementValueMultiplier,
      // ADR-035 3.1 — PILOT abates the city real line too; `?? 0` keeps
      // pre-3.1 snapshots at no PILOT.
      pilotAbatementPct: i.step8.pilotAbatementPct ?? 0,
      pilotAbatementYears: i.step8.pilotAbatementYears ?? 0,
      // ADR-035 3.4 — `?? ` keeps pre-3.4 snapshots at zero rebate.
      salesTaxRebatePct: i.step8.salesTaxRebatePct ?? 0,
      salesTaxRebateYears: i.step8.salesTaxRebateYears ?? 0,
      // ADR-035 simplified TIF — `?? ` keeps pre-TIF snapshots at zero capture.
      tifCaptureShare: i.step8.tifCaptureShare ?? 0,
      tifYears: i.step8.tifYears ?? 0,
      discountRate: i.step8.discountRate,
    },
    // ADR-036 tier presence — omit a tier the jurisdiction lacks (New England
    // towns, consolidated city-counties). `?? true` keeps pre-036 snapshots on
    // all three tiers. The engine already treats these as optional.
    ...((i.step6.hasCountyPropertyTax ?? true)
      ? {
          countyRates: {
            propertyTaxRate: i.step6.countyPropertyTaxRate,
            discountRate: i.step8.discountRate,
            // ADR-035 3.1 — PILOT abates the county levy alongside the city.
            abatementPct: i.step8.pilotAbatementPct ?? 0,
            abatementYears: i.step8.pilotAbatementYears ?? 0,
          },
        }
      : {}),
    ...((i.step6.hasSeparateSchoolDistrict ?? true)
      ? {
          isdRates: {
            propertyTaxRate: i.step6.isdPropertyTaxRate,
            discountRate: i.step8.discountRate,
            // ADR-034 2.3 — `?? 1` keeps pre-2.3 snapshots at full local retention.
            localRetentionShare: i.step6.schoolLocalRetentionShare ?? 1,
            // ADR-035 3.1 — PILOT abates the ISD levy alongside the city.
            abatementPct: i.step8.pilotAbatementPct ?? 0,
            abatementYears: i.step8.pilotAbatementYears ?? 0,
          },
        }
      : {}),
    ...(i.step6.specialDistrictName && i.step6.specialDistrictPropertyTaxRate != null
      ? {
          specialDistrictRates: {
            name: i.step6.specialDistrictName,
            propertyTaxRate: i.step6.specialDistrictPropertyTaxRate,
            costPerWorker: i.step6.specialDistrictCostPerWorker ?? 0,
            costInflationRate: i.step6.costInflationRate,
            discountRate: i.step8.discountRate,
          },
        }
      : {}),
  };
}

/* ===================================================================== */
/*  Legacy re-exports (backward compat during Sprint 6 migration)         */
/*  Remove when wizard + API no longer reference the v0 names.            */
/* ===================================================================== */

/** @deprecated Use WizardStep1 instead */
export const ProjectDescription = WizardStep1;
/** @deprecated Use WizardStep1 instead */
export type ProjectDescription = WizardStep1;

/** @deprecated Use WizardStep2 instead */
export const CapitalInvestment = WizardStep2;
/** @deprecated Use WizardStep2 instead */
export type CapitalInvestment = WizardStep2;

/** @deprecated Use WizardStep3 instead */
export const Employment = WizardStep3;
/** @deprecated Use WizardStep3 instead */
export type Employment = WizardStep3;

/** @deprecated Use WizardStep7 instead */
export const RimsMultipliers = WizardStep7;
/** @deprecated Use WizardStep7 instead */
export type RimsMultipliers = WizardStep7;

/* ===================================================================== */
/*  ADR-030 — Expansion baseline gate                                     */
/* ===================================================================== */

/**
 * The four baseline fields that definitionally must be non-zero for any
 * real expansion project: an expansion always has existing workers or an
 * existing building/equipment on the tax roll. If all four are zero, the
 * project is not actually an expansion — the analyst likely picked the
 * wrong project type.
 *
 * Used by the wizard (pre-submit), the calculate route (server-side
 * enforcement), and the Channel 3 ingest endpoint. Retention analyses
 * are exempt: baseline=0 is the intended framing per ADR-030 Part A.
 */
export function isExpansionBaselineMissing(inputs: AnalysisInput): boolean {
  if (inputs.step1.projectType !== 'expansion') return false;
  return (
    inputs.step3.baselineDirectJobs === 0 &&
    inputs.step3.baselineDirectAnnualPayroll === 0 &&
    inputs.step5.baselineAssessedRealProperty === 0 &&
    inputs.step5.baselineAssessedBpp === 0
  );
}

/**
 * ADR-031 Channel 3 guard: retention analyses MUST carry a counterfactual
 * block. The avoided-loss pipeline collapses to zero without it; Channel 3
 * (API ingest) hard-fails with `400 RETENTION_COUNTERFACTUAL_REQUIRED`
 * rather than silently publishing a zero-impact report.
 */
export function isRetentionCounterfactualMissing(inputs: AnalysisInput): boolean {
  if (inputs.step1.projectType !== 'retention') return false;
  return inputs.retentionCounterfactual == null;
}

/**
 * ADR-031 Part D: when `probabilityOfDeparture > 0.5` the analyst must
 * supply `evidence` — a free-text justification at minimum (LOI, board
 * resolution, relocation quote). Mirrors ADR-029's "high-impact override
 * needs justification" pattern. Returns true when evidence is required
 * but missing or blank.
 */
export function isRetentionEvidenceMissing(inputs: AnalysisInput): boolean {
  if (inputs.step1.projectType !== 'retention') return false;
  const cf = inputs.retentionCounterfactual;
  if (cf == null) return false; // separate gate — isRetentionCounterfactualMissing
  if (cf.probabilityOfDeparture <= 0.5) return false;
  return !cf.evidence || cf.evidence.trim().length === 0;
}

/**
 * ADR-030 Channel 3 guard: new_startup analyses should not carry non-zero
 * baselines. If the caller supplied any, they probably picked the wrong
 * project type. Returns true if the inputs are inconsistent.
 */
export function hasUnexpectedBaselines(inputs: AnalysisInput): boolean {
  if (inputs.step1.projectType !== 'new_startup') return false;
  return (
    inputs.step2.baselineTotalConstructionSpending > 0 ||
    inputs.step2.baselineLand > 0 ||
    inputs.step2.baselineBuildings > 0 ||
    inputs.step2.baselineMachineryEquipment > 0 ||
    inputs.step2.baselineFfe > 0 ||
    inputs.step2.baselineInventory > 0 ||
    inputs.step3.baselineDirectJobs > 0 ||
    inputs.step3.baselineDirectAnnualPayroll > 0 ||
    inputs.step4.baselineAnnualTaxableSales > 0 ||
    inputs.step4.baselineAnnualRoomRevenue > 0 ||
    inputs.step4.baselineAnnualMixedBeverageReceipts > 0 ||
    inputs.step5.baselineAssessedRealProperty > 0 ||
    inputs.step5.baselineAssessedBpp > 0 ||
    inputs.step5.baselineAssessedInventory > 0
  );
}
